import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { GridRenderCellParams } from "@mui/x-data-grid";

import { useConfig } from "~/contexts/config";
import { getAccountURL, getCustomerURL } from "~/services/stripe";

interface UserCellProps extends GridRenderCellParams {
  userTransactionRole: "buyer" | "seller";
}

export function UserCell({ value, row, userTransactionRole }: UserCellProps) {
  const { stripe: stripe } = useConfig();
  const email = value;
  const isGuest =
    userTransactionRole === "buyer" ? row.buyer_is_guest : row.seller_is_guest;

  const guestEmail =
    userTransactionRole === "buyer"
      ? row.buyer_guest_email
      : row.seller_guest_email;

  const displayEmail = email || guestEmail;

  const stripeCustomerId =
    userTransactionRole === "buyer"
      ? row.buyer_stripe_customer_id
      : row.seller_stripe_customer_id;
  const stripeAccountId =
    userTransactionRole === "buyer"
      ? row.buyer_stripe_account_id
      : row.seller_stripe_account_id;

  const stripePlatformId = row.stripe_platform_id;

  const isFraudulent =
    userTransactionRole === "buyer"
      ? row.buyer_fraudulent
      : row.seller_fraudulent;

  if (!displayEmail) {
    return <span>-</span>;
  }

  let stripeUrl: string | null = null;
  let stripeLinkText = "";

  if (userTransactionRole === "buyer") {
    if (stripe.dashboardUrl && stripePlatformId && stripeCustomerId) {
      stripeUrl = getCustomerURL(stripe, stripePlatformId, stripeCustomerId);
      stripeLinkText = "Stripe customer";
    }
  } else {
    if (stripe.dashboardUrl && stripePlatformId && stripeAccountId) {
      stripeUrl = getAccountURL(stripe, stripePlatformId, stripeAccountId);
      stripeLinkText = "Stripe connected account";
    }
  }

  return (
    <Stack flexDirection="column" alignItems="flex-start">
      <Typography variant="body2">{displayEmail}</Typography>
      <Stack direction="row" alignItems="center">
        {isFraudulent && (
          <Typography variant="caption" color="error.main" fontWeight="bold">
            High-risk&nbsp;
          </Typography>
        )}
        {isGuest && (
          <Typography variant="caption" color="warning.main" fontWeight="bold">
            Guest&nbsp;
          </Typography>
        )}
        {stripeUrl && (
          <Link
            variant="body2"
            href={stripeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            {stripeLinkText}
          </Link>
        )}
      </Stack>
    </Stack>
  );
}
